import React from 'react';
import { Box, Button, Chip, CircularProgress, Collapse, Typography } from '@mui/material';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import { useTranslation } from 'react-i18next';
import LastSql from './LastSql';
import { stageLabel } from './stageLabels';
import { CHIP_WRAP_SX } from '../layoutSx';

/** Category-specific heading ("This metric isn't in your data"). */
export const diagnosisTitle = (t, diagnosis) => t(`Dashboards.diagnosis.title.${(diagnosis && diagnosis.category) || 'GENERIC'}`,
  { defaultValue: t('Dashboards.diagnosis.title.GENERIC') });

/** "table (col, col, …)" lines of available_data. */
export function AvailableData({ data, maxTables = 3, maxColumns = 8 }) {
  const { t } = useTranslation();
  if (!data || !data.length) return null;
  return (
    <Box sx={{ minWidth: 0 }} data-testid="available-data">
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{t('Dashboards.diagnosis.availableData')}</Typography>
      {data.slice(0, maxTables).map((a) => (
        <Typography key={a.table} variant="caption" color="text.secondary" display="block" sx={{ overflowWrap: 'anywhere' }}>
          <Box component="span" sx={{ fontFamily: 'monospace' }}>{a.table}</Box>
          {a.columns && a.columns.length > 0 && ` — ${a.columns.slice(0, maxColumns).join(', ')}${a.columns.length > maxColumns ? ', …' : ''}`}
        </Typography>
      ))}
    </Box>
  );
}

/** Stage, raw error and last SQL, collapsed by default. */
export function TechnicalDetails({ stage, message, lastSql, defaultOpen = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(defaultOpen);
  if (!stage && !message && !lastSql) return null;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Button size="small" onClick={() => setOpen((o) => !o)} aria-expanded={open} sx={{ textTransform: 'none', px: 0.5, minWidth: 0 }}>
        {t('Dashboards.diagnosis.technicalDetails')}
      </Button>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: 0.5, minWidth: 0, overflowWrap: 'anywhere' }}>
          {stage && (
            <Typography variant="caption" display="block">{t('Dashboards.repair.failedAt', { stage: stageLabel(t, stage) })}</Typography>
          )}
          {message && <Typography variant="caption" color="error" display="block">{message}</Typography>}
          <LastSql sql={lastSql} maxHeight={140} />
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * Guided failed-widget card (inside the widget cell, scrolls within it): category heading and plain
 * explanation first, the data that exists, one-click suggestions, then "answer / add details" (guided
 * dialog), "Fix with my own knowledge" (expert dialog) and Retry — prominent only for TECHNICAL.
 * Technical details stay collapsed.
 *
 * @param onAction({ type: 'retry' | 'guided' | 'expert' | 'suggestion', suggestion? })
 */
export default function GuidedFailureCard({
  diagnosis, diagnosisLoading, failure, canRepair, disabled, onAction,
}) {
  const { t } = useTranslation();
  const technical = diagnosis && diagnosis.category === 'TECHNICAL';
  const suggestions = (diagnosis && diagnosis.suggestions) || [];
  const questions = (diagnosis && diagnosis.questions) || [];
  const act = (a) => { if (onAction) onAction(a); };
  return (
    <Box data-testid="widget-failed"
      sx={{ p: 1, height: '100%', boxSizing: 'border-box', overflowY: 'auto', overflowX: 'hidden', minWidth: 0,
        overflowWrap: 'anywhere', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box>
        <Typography variant="subtitle2" color={technical ? 'error' : 'warning.dark'} sx={{ fontWeight: 700 }} data-testid="diagnosis-title">
          {diagnosis ? diagnosisTitle(t, diagnosis) : t('Dashboards.diagnosis.title.GENERIC')}
        </Typography>
        {diagnosisLoading && !diagnosis && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="diagnosis-loading">
            <CircularProgress size={12} />
            <Typography variant="caption" color="text.secondary">{t('Dashboards.diagnosis.loading')}</Typography>
          </Box>
        )}
        <Typography variant="body2" sx={{ mt: 0.25 }}>
          {(diagnosis && diagnosis.explanation) || (failure && failure.message) || t('Dashboards.widget.loadFailed')}
        </Typography>
      </Box>
      {diagnosis && <AvailableData data={diagnosis.available_data} maxTables={2} maxColumns={6} />}
      {canRepair && suggestions.length > 0 && (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}>{t('Dashboards.diagnosis.suggestionsTitle')}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {suggestions.map((s) => (
              <Chip key={s.id} size="small" color="primary" variant="outlined" icon={<TipsAndUpdatesOutlinedIcon />}
                label={s.label} title={s.hint} disabled={disabled} clickable
                onClick={() => act({ type: 'suggestion', suggestion: s })} sx={CHIP_WRAP_SX} />
            ))}
          </Box>
        </Box>
      )}
      {canRepair && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {!technical && (
            <Button size="small" variant="contained" startIcon={<TipsAndUpdatesOutlinedIcon />} disabled={disabled}
              onClick={() => act({ type: 'guided' })} sx={{ textTransform: 'none' }}>
              {questions.length ? t('Dashboards.repair.guidedOpenQuestions', { count: questions.length })
                : t('Dashboards.repair.guidedOpen')}
            </Button>
          )}
          <Button size="small" variant={technical ? 'contained' : 'text'} startIcon={<ReplayIcon />} disabled={disabled}
            onClick={() => act({ type: 'retry' })} sx={{ textTransform: 'none' }}>
            {t('Dashboards.repair.retry')}
          </Button>
          <Button size="small" variant="outlined" startIcon={<BuildOutlinedIcon />} disabled={disabled}
            onClick={() => act({ type: 'expert' })} sx={{ textTransform: 'none' }}>
            {t('Dashboards.repair.expert')}
          </Button>
        </Box>
      )}
      <TechnicalDetails stage={failure && failure.stage} message={failure && failure.message} lastSql={failure && failure.lastSql} />
    </Box>
  );
}
